import { z } from "zod";

/** Shared, versioned contracts for obituary research and NC intestacy triage. */
// v3 keeps search and source retrieval separate from identity resolution. It
// compares every retained candidate against a source-neutral identity packet,
// and only a unique, evidence-grounded AI resolution can publish obituary text.
// The version is part of the durable cache key so no v1/v2 conclusion can be
// silently reused as a v3 match.
export const OBITUARY_LOOKUP_VERSION = "obituary-lookup-v3-ai-identity-title-nc-v3" as const;
export const NC_INTESTACY_OBITUARY_ANALYSIS_VERSION = "nc-intestacy-obituary-analysis-v2-match-gated" as const;
export const OBITUARY_LOOKUP_RESULT_TYPE = "obituary_lookup_result" as const;
export const NC_INTESTACY_ANALYSIS_RESULT_TYPE = "nc_intestacy_obituary_analysis_result" as const;
export const OBITUARY_SOURCE_TEXT_LIMIT = 45_000;

/**
 * Count-priority snapshots, not share calculations.  Each transition is an
 * official NC session-law effective date that can alter who is eligible to be
 * counted.  The extractor deliberately does not decide the factual predicates
 * (adoption, paternity, or 120-hour survival) from obituary wording alone.
 */
export const NC_INTESTACY_COUNT_SNAPSHOTS = [
  { id: "nc-intestacy-count-1960-07-01", from: "1960-07-01", to: "1974-03-31", source: "https://www.ncleg.gov/EnactedLegislation/Statutes/HTML/ByChapter/Chapter_29.html", note: "1959 rewrite effective July 1, 1960; later children-born-out-of-wedlock transitions are not assumed." },
  { id: "nc-intestacy-count-1974-04-01", from: "1974-04-01", to: "1975-03-13", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/1973-1974/SL1973-1062.html", note: "Paternity adjudication transition ratified April 1, 1974." },
  { id: "nc-intestacy-count-1975-03-14", from: "1975-03-14", to: "1977-06-15", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/1975-1976/SL1975-54.html", note: "Children-born-out-of-wedlock succession transition ratified March 14, 1975." },
  { id: "nc-intestacy-count-1977-06-16", from: "1977-06-16", to: "1977-08-31", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/1977-1978/SL1977-591.html", note: "Acknowledgment of paternity must occur during both lifetimes from June 16, 1977." },
  { id: "nc-intestacy-count-1977-09-01", from: "1977-09-01", to: "2007-09-30", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/PDF/1977-1978/SL1977-757.pdf", note: "Recognizes qualifying father as parent for estates of decedents dying on or after September 1, 1977." },
  { id: "nc-intestacy-count-2007-10-01", from: "2007-10-01", to: "2011-12-31", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/2007-2008/SL2007-132.html", note: "120-hour survivorship rule applies to deaths on or after October 1, 2007." },
  { id: "nc-intestacy-count-2012-01-01", from: "2012-01-01", to: "2013-06-25", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/2011-2012/SL2011-344.html", note: "Chapter 29 rewrite effective January 1, 2012." },
  { id: "nc-intestacy-count-2013-06-26", from: "2013-06-26", to: "2025-07-08", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/2013-2014/SL2013-198.html", note: "Children-born-out-of-wedlock transition applies to deaths on or after June 26, 2013." },
  { id: "nc-intestacy-count-2025-07-09", from: "2025-07-09", to: "2026-07-26", source: "https://www.ncleg.gov/EnactedLegislation/SessionLaws/HTML/2025-2026/SL2025-75.html", note: "Current recorded snapshot; paternity transition applies to deaths on or after July 9, 2025." }
] as const;
export type NcIntestacyCountSnapshot = (typeof NC_INTESTACY_COUNT_SNAPSHOTS)[number];

export function ncIntestacyCountSnapshotForDeathDate(deathDate: string | null | undefined): NcIntestacyCountSnapshot | null {
  if (!deathDate || !/^\d{4}-\d{2}-\d{2}$/.test(deathDate)) return null;
  return NC_INTESTACY_COUNT_SNAPSHOTS.find((snapshot) => deathDate >= snapshot.from && deathDate <= snapshot.to) ?? null;
}

export const ObituaryIdentitySourceSchema = z.object({
  id: z.string().trim().min(1).max(120),
  type: z.enum(["attom", "skip_trace", "tax_record", "deed_record", "court_record", "user_provided", "other"]),
  label: z.string().trim().min(1).max(200).optional(),
  recordId: z.string().trim().min(1).max(240).optional()
});
export type ObituaryIdentitySource = z.infer<typeof ObituaryIdentitySourceSchema>;

export const ObituaryIdentityNameSchema = z.object({
  first: z.string().trim().min(1).max(100),
  middle: z.string().trim().max(100).optional().default(""),
  last: z.string().trim().min(1).max(140),
  suffix: z.string().trim().max(40).optional().default("")
});
export type ObituaryIdentityName = z.infer<typeof ObituaryIdentityNameSchema>;

export const ObituaryIdentityAddressSchema = z.object({
  street: z.string().trim().min(1).max(180),
  city: z.string().trim().min(1).max(100),
  state: z.string().trim().min(2).max(64),
  postalCode: z.string().trim().min(3).max(16)
});
export type ObituaryIdentityAddress = z.infer<typeof ObituaryIdentityAddressSchema>;

/**
 * Property records are sometimes incomplete while the mailing record is
 * complete.  Empty property fields are explicit unknowns; they are never
 * filled from mailing data.  Mailing addresses continue to use the strict
 * complete-address contract above.
 */
export const ObituaryPropertyAddressSchema = z.object({
  street: z.string().trim().max(180).default(""),
  city: z.string().trim().max(100).default(""),
  state: z.string().trim().max(64).default(""),
  postalCode: z.string().trim().max(16).default("")
});
export type ObituaryPropertyAddress = z.infer<typeof ObituaryPropertyAddressSchema>;

export const ObituaryKnownLocationSchema = z.object({
  city: z.string().trim().min(1).max(100),
  state: z.string().trim().min(2).max(64),
  postalCode: z.string().trim().max(16).optional().default("")
});
export type ObituaryKnownLocation = z.infer<typeof ObituaryKnownLocationSchema>;

export const ObituaryIdentityPacketSchema = z.object({
  primaryName: ObituaryIdentityNameSchema,
  propertyAddress: ObituaryPropertyAddressSchema,
  mailingAddress: ObituaryIdentityAddressSchema.optional(),
  aliases: z.array(z.string().trim().min(1).max(300)).max(25).optional().default([]),
  age: z.number().int().min(0).max(130).optional(),
  dateOfBirth: z.string().date().optional(),
  spouseOrCoOwners: z.array(z.string().trim().min(1).max(300)).max(50).optional().default([]),
  relatives: z.array(z.string().trim().min(1).max(300)).max(100).optional().default([]),
  knownLocations: z.array(ObituaryKnownLocationSchema).max(50).optional().default([]),
  sources: z.array(ObituaryIdentitySourceSchema).max(25).optional().default([]),
  factSources: z.record(z.string().trim().min(1).max(160), z.array(z.string().trim().min(1).max(120)).max(25)).optional().default({})
});
export type ObituaryIdentityPacket = z.infer<typeof ObituaryIdentityPacketSchema>;

const LegacyObituaryLookupInputSchema = z.object({
  street: z.string().trim().min(1).max(180),
  city: z.string().trim().min(1).max(100),
  state: z.string().trim().min(2).max(64),
  postalCode: z.string().trim().min(3).max(16),
  ownerFirst: z.string().trim().min(1).max(100),
  ownerMiddle: z.string().trim().max(100).optional().default(""),
  ownerLast: z.string().trim().min(1).max(140)
});
export type LegacyObituaryLookupInput = z.infer<typeof LegacyObituaryLookupInputSchema>;

function legacyObituaryLookupInput(value: unknown): unknown {
  const legacy = LegacyObituaryLookupInputSchema.safeParse(value);
  if (!legacy.success) return value;
  return {
    identityPacket: {
      primaryName: {
        first: legacy.data.ownerFirst,
        middle: legacy.data.ownerMiddle,
        last: legacy.data.ownerLast,
        suffix: ""
      },
      propertyAddress: {
        street: legacy.data.street,
        city: legacy.data.city,
        state: legacy.data.state,
        postalCode: legacy.data.postalCode
      },
      aliases: [],
      spouseOrCoOwners: [],
      relatives: [],
      knownLocations: [],
      sources: [],
      factSources: {}
    }
  };
}

/**
 * The preprocess keeps old Sheet formulas readable during rollout, but parsed
 * and persisted v3 input is always the source-neutral identity-packet shape.
 */
export const ObituaryLookupInputSchema = z.preprocess(
  legacyObituaryLookupInput,
  z.object({ identityPacket: ObituaryIdentityPacketSchema })
);
export type ObituaryLookupInput = z.infer<typeof ObituaryLookupInputSchema>;

export const ObituaryCorroboratorFieldSchema = z.enum([
  "middle_name",
  "suffix",
  "alias",
  "date_of_birth",
  "age",
  "spouse_or_co_owner",
  "relative",
  "property_address",
  "mailing_address",
  "known_location",
  "north_carolina_anchor",
  "other"
]);
export type ObituaryCorroboratorField = z.infer<typeof ObituaryCorroboratorFieldSchema>;

export const ObituaryIdentityEvidenceSchema = z.object({
  field: ObituaryCorroboratorFieldSchema,
  targetValue: z.string().trim().min(1).max(500),
  candidateValue: z.string().trim().min(1).max(500),
  candidateId: z.string().trim().min(1).max(80),
  excerpt: z.string().trim().min(1).max(1_200)
});
export type ObituaryIdentityEvidence = z.infer<typeof ObituaryIdentityEvidenceSchema>;

export const ObituaryIdentityConflictSchema = z.object({
  field: z.string().trim().min(1).max(160),
  targetValue: z.string().trim().max(500).nullable(),
  candidateValue: z.string().trim().max(500).nullable(),
  candidateId: z.string().trim().min(1).max(80),
  excerpt: z.string().trim().min(1).max(1_200),
  material: z.boolean()
});
export type ObituaryIdentityConflict = z.infer<typeof ObituaryIdentityConflictSchema>;

export const ObituaryCandidateAssessmentSchema = z.object({
  candidateId: z.string().trim().min(1).max(80),
  entityId: z.string().trim().min(1).max(120).nullable(),
  disposition: z.enum(["selected_same_person", "plausible_same_person", "different_person", "insufficient_evidence"]),
  decedentName: z.string().trim().min(1).max(300).nullable(),
  deathDate: z.string().date().nullable(),
  nameEvidence: z.object({
    targetValue: z.string().trim().min(1).max(300),
    candidateValue: z.string().trim().min(1).max(300),
    excerpt: z.string().trim().min(1).max(1_200)
  }).nullable(),
  rationale: z.string().trim().min(1).max(1_000)
});
export type ObituaryCandidateAssessment = z.infer<typeof ObituaryCandidateAssessmentSchema>;

export const ObituaryIdentityResolutionSchema = z.object({
  decision: z.enum(["confirmed_match", "ambiguous_review", "no_match"]),
  selectedEntityId: z.string().trim().min(1).max(120).nullable(),
  selectedCandidateIds: z.array(z.string().trim().min(1).max(80)).max(12),
  decedentName: z.string().trim().min(1).max(300).nullable(),
  deathDate: z.string().date().nullable(),
  corroborators: z.array(ObituaryIdentityEvidenceSchema).max(50),
  conflicts: z.array(ObituaryIdentityConflictSchema).max(50),
  candidateAssessments: z.array(ObituaryCandidateAssessmentSchema).max(12),
  explanation: z.string().trim().min(1).max(2_000)
});
export type ObituaryIdentityResolution = z.infer<typeof ObituaryIdentityResolutionSchema>;

export const PERSON_SPECIFIC_CORROBORATOR_FIELDS = [
  "spouse_or_co_owner",
  "relative",
  "property_address",
  "mailing_address",
  "date_of_birth",
  "age",
  "alias"
] as const satisfies readonly ObituaryCorroboratorField[];
export type PersonSpecificCorroboratorField = (typeof PERSON_SPECIFIC_CORROBORATOR_FIELDS)[number];

export const OBITUARY_NAME_PLUS_STATE_INSUFFICIENT_REASON =
  "Name plus a state reference is insufficient identity evidence; no independent person-specific corroborator was grounded in selected source text and matched to owner facts.";

export type ObituaryIdentityRejectedCorroborator = {
  field: string;
  candidateId: string;
  reason: string;
};

export type VerifiedObituaryIdentityResolution = {
  resolution: ObituaryIdentityResolution;
  decision: "confirmed_match" | "ambiguous_review" | "no_match";
  reviewReasons: string[];
  corroboratingFields: ObituaryCorroboratorField[];
  personSpecificFields: PersonSpecificCorroboratorField[];
  rejectedCorroborators: ObituaryIdentityRejectedCorroborator[];
  acceptanceExplanation: string;
};

export type ObituaryIdentityLookupStatus =
  | "complete"
  | "obituary_no_match"
  | "obituary_incomplete_evidence"
  | "obituary_ambiguous_match";

function identityDecisionHasHardFail(reviewReasons: string[]): boolean {
  return reviewReasons.some((reason) =>
    /material middle-name conflict/i.test(reason)
    || /different explicit death dates/i.test(reason)
  );
}

function isDifferentPersonRefusal(
  verified: Pick<VerifiedObituaryIdentityResolution, "decision" | "reviewReasons"> & {
    resolution?: Pick<ObituaryIdentityResolution, "candidateAssessments">
  }
): boolean {
  const assessments = verified.resolution?.candidateAssessments ?? [];
  return assessments.length > 0
    && assessments.every((assessment) => assessment.disposition === "different_person");
}

/**
 * Stamp for a verified identity decision. Name + NC with no person-specific
 * corroborator is incomplete evidence (repairable), not a terminal no-match.
 * Extra bookkeeping reasons must not wipe that recovery path. A different-person
 * refusal remains a terminal no-match even when the insufficient-evidence
 * sentence is also recorded because selected IDs were empty.
 */
export function obituaryLookupStatusForIdentityDecision(
  verified: Pick<VerifiedObituaryIdentityResolution, "decision" | "reviewReasons"> & {
    resolution?: Pick<ObituaryIdentityResolution, "candidateAssessments">
  }
): ObituaryIdentityLookupStatus {
  if (verified.decision === "confirmed_match") return "complete";
  if (verified.decision === "ambiguous_review") return "obituary_ambiguous_match";
  if (
    verified.reviewReasons.includes(OBITUARY_NAME_PLUS_STATE_INSUFFICIENT_REASON)
    && !identityDecisionHasHardFail(verified.reviewReasons)
    && !isDifferentPersonRefusal(verified)
  ) {
    return "obituary_incomplete_evidence";
  }
  return "obituary_no_match";
}

export const ObituarySourceSchema = z.object({
  sourceNumber: z.number().int().min(1).max(3),
  publisher: z.string().trim().min(1).max(240),
  canonicalUrl: z.string().url().refine((value) => value.startsWith("https://"), "source URL must use HTTPS"),
  publicationDate: z.string().date().nullable(),
  retrievedAt: z.string().datetime(),
  coreText: z.string().trim().min(1),
  coreTextSha256: z.string().regex(/^[a-f0-9]{64}$/),
  title: z.string().trim().max(500).nullable().optional(),
  googleQuery: z.string().trim().min(1).max(1_000).nullable().optional(),
  organicRank: z.number().int().min(1).max(10).nullable().optional(),
  artifactUris: z.array(z.string().min(1)).default([])
});
export type ObituarySource = z.infer<typeof ObituarySourceSchema>;

export const ObituarySourceEnvelopeSchema = z.object({
  sources: z.array(ObituarySourceSchema).min(1).max(3)
});
export type ObituarySourceEnvelope = z.infer<typeof ObituarySourceEnvelopeSchema>;

export const FamilyRelationshipSchema = z.enum([
  "spouse", "child", "grandchild", "great_grandchild", "parent", "sibling",
  "niece_nephew", "grandparent", "aunt_uncle", "cousin", "stepchild",
  "foster_child", "child_in_law", "partner", "fiance", "former_spouse", "other"
]);
export type FamilyRelationship = z.infer<typeof FamilyRelationshipSchema>;

export const ApparentSurvivalSchema = z.enum(["apparently_survived", "apparently_predeceased", "unknown"]);
export type ApparentSurvival = z.infer<typeof ApparentSurvivalSchema>;

export const FamilyPersonSchema = z.object({
  id: z.string().trim().min(1).max(160),
  name: z.string().trim().min(1).max(300),
  normalizedName: z.string().trim().min(1).max(300).nullable().optional(),
  relationship: FamilyRelationshipSchema,
  survival: ApparentSurvivalSchema,
  individuallyNamed: z.boolean(),
  /** Parent child/branch ID when representation is asserted by source text. */
  parentBranchId: z.string().trim().min(1).max(160).nullable().optional(),
  evidenceExcerpt: z.string().trim().min(1).max(1_200),
  ambiguityFlags: z.array(z.string().trim().min(1).max(120)).default([])
});
export type FamilyPerson = z.infer<typeof FamilyPersonSchema>;

export const FamilyGraphSchema = z.object({
  decedentName: z.string().trim().min(1).max(300).nullable(),
  deathDate: z.string().date().nullable(),
  persons: z.array(FamilyPersonSchema).max(500),
  /** The model may report family wording; it cannot choose legal status. */
  maritalStatementComplete: z.boolean().default(false),
  sourceConflicts: z.array(z.string().trim().min(1).max(500)).default([]),
  extractionLimitations: z.array(z.string().trim().min(1).max(500)).default([])
});
export type FamilyGraph = z.infer<typeof FamilyGraphSchema>;

export const SpouseStatusSchema = z.enum(["true", "false", "unknown"]);
export type SpouseStatus = z.infer<typeof SpouseStatusSchema>;
export const HeirCountStatusSchema = z.enum([
  "family_structure_complete",
  "source_supported_lower_bound",
  "insufficient_source_evidence",
  "historical_rules_unverified_current_snapshot_proxy"
]);
export type HeirCountStatus = z.infer<typeof HeirCountStatusSchema>;

export const ApparentHeirSchema = z.object({
  id: z.string(),
  name: z.string(),
  relationship: FamilyRelationshipSchema,
  survival: ApparentSurvivalSchema,
  branch: z.string().nullable(),
  included: z.boolean(),
  reason: z.string(),
  evidenceExcerpt: z.string()
});
export type ApparentHeir = z.infer<typeof ApparentHeirSchema>;

export const NcIntestacyAnalysisSchema = z.object({
  result_type: z.literal(NC_INTESTACY_ANALYSIS_RESULT_TYPE),
  version: z.literal("v1"),
  obituaryTextSha256: z.string().regex(/^[a-f0-9]{64}$/),
  familyGraph: FamilyGraphSchema,
  spouseStatus: SpouseStatusSchema,
  apparentSpouseNames: z.array(z.string()),
  spouseEvidence: z.array(z.string()),
  apparentHeirEstimate: z.number().int().nonnegative(),
  heirCountStatus: HeirCountStatusSchema,
  controllingHeirClass: z.string(),
  branchCount: z.number().int().nonnegative(),
  apparentHeirs: z.array(ApparentHeirSchema),
  rulesetId: z.string(),
  rulesetEffectiveFrom: z.string().date().nullable(),
  rulesetEffectiveTo: z.string().date().nullable(),
  heirConfidence: z.number().int().min(0).max(100),
  spouseConfidence: z.number().int().min(0).max(100),
  heirConfidenceFactors: z.record(z.string(), z.number().int().nonnegative()),
  spouseConfidenceFactors: z.record(z.string(), z.number().int().nonnegative()),
  limitations: z.array(z.string())
});
export type NcIntestacyAnalysis = z.infer<typeof NcIntestacyAnalysisSchema>;

export function canonicalObituaryText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const normalized = value.normalize("NFC").replace(/\r\n?/g, "\n").split("\n").map((line) => line.replace(/[ \t]+$/g, "")).join("\n").replace(/^\n+|\n+$/g, "");
  return normalized || null;
}

function canonicalIdentityToken(value: string): string {
  return value.normalize("NFKD").replace(/\p{M}/gu, "").trim().replace(/\s+/g, " ").toUpperCase();
}

function canonicalIdentityAddress(address: ObituaryPropertyAddress | ObituaryIdentityAddress): Record<string, string> {
  return {
    street: canonicalIdentityToken(address.street),
    city: canonicalIdentityToken(address.city),
    state: canonicalIdentityToken(address.state),
    postalCode: canonicalIdentityToken(address.postalCode)
  };
}

function canonicalIdentityLocation(location: ObituaryKnownLocation): Record<string, string> {
  return {
    city: canonicalIdentityToken(location.city),
    state: canonicalIdentityToken(location.state),
    postalCode: canonicalIdentityToken(location.postalCode)
  };
}

export function canonicalObituaryLookupInput(input: ObituaryLookupInput | LegacyObituaryLookupInput): string {
  const parsed = ObituaryLookupInputSchema.parse(input);
  const packet = parsed.identityPacket;
  const factSources = Object.fromEntries(Object.entries(packet.factSources)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([field, sourceIds]) => [canonicalIdentityToken(field), [...new Set(sourceIds.map(canonicalIdentityToken))].sort()]));
  return JSON.stringify({
    primaryName: {
      first: canonicalIdentityToken(packet.primaryName.first),
      middle: canonicalIdentityToken(packet.primaryName.middle),
      last: canonicalIdentityToken(packet.primaryName.last),
      suffix: canonicalIdentityToken(packet.primaryName.suffix)
    },
    propertyAddress: canonicalIdentityAddress(packet.propertyAddress),
    mailingAddress: packet.mailingAddress ? canonicalIdentityAddress(packet.mailingAddress) : null,
    aliases: [...new Set(packet.aliases.map(canonicalIdentityToken))].sort(),
    age: packet.age ?? null,
    dateOfBirth: packet.dateOfBirth ?? null,
    spouseOrCoOwners: [...new Set(packet.spouseOrCoOwners.map(canonicalIdentityToken))].sort(),
    relatives: [...new Set(packet.relatives.map(canonicalIdentityToken))].sort(),
    knownLocations: packet.knownLocations.map(canonicalIdentityLocation)
      .sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
    // Source-row provenance is retained in the durable input and receipt but
    // is deliberately not part of cache identity.  Otherwise two source rows
    // for the same person/property create duplicate paid lookups.  The stable
    // scope remains the normalized person/property/location packet above.
    sources: [],
    factSources: {}
  });
}

export function obituaryIdentityDisplayName(packetInput: ObituaryIdentityPacket): string {
  const packet = ObituaryIdentityPacketSchema.parse(packetInput);
  return [packet.primaryName.first, packet.primaryName.middle, packet.primaryName.last, packet.primaryName.suffix].filter(Boolean).join(" ");
}

function identityAddressDisplay(address: ObituaryPropertyAddress | ObituaryIdentityAddress): string {
  return [address.street, address.city, address.state, address.postalCode].filter(Boolean).join(", ");
}

function identityLocationDisplay(location: ObituaryKnownLocation): string {
  return [location.city, location.state, location.postalCode].filter(Boolean).join(", ");
}

function normalizeIdentityComparison(value: string): string {
  return value.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function removeQuotedNameNicknames(value: string): string {
  return value
    .replace(/["“”][^"“”]+["“”]/g, " ")
    .replace(/(^|\s)['’][^'’]+['’](?=\s|$)/g, "$1 ")
    .replace(/\([^)]*\)/g, " ");
}

function normalizedNameTokens(value: string): string[] {
  return normalizeIdentityComparison(value).split(" ").filter(Boolean);
}

function comparisonNameTokens(value: string): string[] {
  return normalizedNameTokens(removeQuotedNameNicknames(value));
}

const NAME_SUFFIXES = new Set(["jr", "junior", "sr", "senior", "ii", "iii", "iv", "v", "vi", "esq", "md", "phd"]);

function nameTokensWithoutSuffix(value: string): string[] {
  const tokens = comparisonNameTokens(value);
  return tokens.length > 1 && NAME_SUFFIXES.has(tokens.at(-1)!) ? tokens.slice(0, -1) : tokens;
}

function middleNamesCompatible(target: string[], candidate: string[]): boolean {
  // A missing middle on either side is not a conflict. When both sides state
  // a middle, compare the known aligned tokens; an initial is compatible with
  // its expanded form while a different explicit name is material.
  if (!target.length || !candidate.length) return true;
  for (let index = 0; index < Math.min(target.length, candidate.length); index += 1) {
    const left = target[index]!;
    const right = candidate[index]!;
    if (left.length === 1 || right.length === 1) {
      if (left[0] !== right[0]) return false;
    } else if (left !== right) {
      return false;
    }
  }
  return true;
}

function explicitMiddleNameConflict(candidate: string, packet: ObituaryIdentityPacket): boolean {
  const targetMiddle = normalizedNameTokens(packet.primaryName.middle);
  if (!targetMiddle.length) return false;
  const candidateTokens = nameTokensWithoutSuffix(candidate);
  const first = normalizeIdentityComparison(packet.primaryName.first);
  const lastTokens = normalizedNameTokens(packet.primaryName.last);
  if (candidateTokens.length < 2 || candidateTokens[0] !== first || !lastTokens.length) return false;
  const candidateLast = candidateTokens.slice(-lastTokens.length);
  if (candidateLast.length !== lastTokens.length || candidateLast.some((token, index) => token !== lastTokens[index])) return false;
  const candidateMiddle = candidateTokens.slice(1, -lastTokens.length);
  return candidateMiddle.length > 0 && !middleNamesCompatible(targetMiddle, candidateMiddle);
}

const DECEDENT_RELATIONSHIP_PREFIX = /\b(?:father|mother|son|daughter|brother|sister|husband|wife|widow|widower|grandfather|grandmother|grandson|granddaughter|uncle|aunt|nephew|niece|cousin|stepfather|stepmother|stepson|stepdaughter|his|her|their|late)\b[,:\s]*$/i;
const SURVIVOR_CLAUSE_PREFIX = /\b(?:survived by|preceded in death by|predeceased by|pallbearers?)\b/i;

function isHeadingOrTitleLine(line: string, index: number): boolean {
  return (index === 0 && line.length <= 240)
    || /^#/.test(line)
    || /^(?:official\s+obituary|obituary\s+(?:for|of)|in\s+loving\s+memory)/i.test(line);
}

function firstBodySentence(text: string): string {
  const lines = text.split(/\r?\n/);
  const bodyStart = lines.findIndex((line, index) => index > 0 && line.trim().length > 0 && !isHeadingOrTitleLine(line.trim(), index));
  const body = (bodyStart >= 0 ? lines.slice(bodyStart) : lines.slice(1)).join(" ").trim();
  return (body.split(/(?<=[.!?])\s+/)[0] ?? "").trim();
}

function mentionIsDecedentContext(text: string, match: RegExpMatchArray, headingLines: Set<string>, leadSentence: string): boolean {
  const index = match.index ?? 0;
  const matched = match[0] ?? "";
  const sentenceStart = Math.max(
    text.lastIndexOf(".", index - 1),
    text.lastIndexOf("!", index - 1),
    text.lastIndexOf("?", index - 1),
    text.lastIndexOf("\n", index - 1)
  ) + 1;
  const before = text.slice(sentenceStart, index);
  if (DECEDENT_RELATIONSHIP_PREFIX.test(before)) return false;
  if (SURVIVOR_CLAUSE_PREFIX.test(before)) return false;
  const lineStart = text.lastIndexOf("\n", index) + 1;
  const lineEnd = text.indexOf("\n", index);
  const line = text.slice(lineStart, lineEnd === -1 ? text.length : lineEnd).trim();
  if (headingLines.has(line)) return true;
  if (leadSentence && leadSentence.toLocaleLowerCase().includes(matched.toLocaleLowerCase())) return true;
  const after = text.slice(index + matched.length, index + matched.length + 80);
  // `of <City>` alone describes any person an obituary names (grandchildren,
  // escorts, officiants). It is decedent context only when the same sentence
  // also states the death.
  const restOfSentence = text.slice(index + matched.length, (() => {
    const stop = [".", "!", "?", "\n"]
      .map((mark) => text.indexOf(mark, index + matched.length))
      .filter((position) => position >= 0);
    return stop.length ? Math.min(...stop) : text.length;
  })());
  const deathPredicate = /\b(?:died|passed away|entered into rest|obituary)\b/i;
  return /^\s*,\s*\d{1,3}\s*,/.test(after)
    || (/^\s+of\s+[A-Z]/.test(after) && deathPredicate.test(restOfSentence))
    || /^\s*[,.\s]*(?:died|passed away|entered into rest|obituary)\b/i.test(after);
}

function candidateHeadingHasMiddleConflict(text: string, packet: ObituaryIdentityPacket): boolean {
  const first = normalizeIdentityComparison(packet.primaryName.first);
  const last = normalizeIdentityComparison(packet.primaryName.last);
  if (!first || !last || !packet.primaryName.middle) return false;
  const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const namePattern = new RegExp(`\\b${escape(first)}\\b(?:[\\s.,'\"()#-]+[A-Za-z][A-Za-z.'-]*){0,3}[\\s.,'\"()#-]+${escape(last)}\\b`, "ig");
  const lines = text.split(/\r?\n/).map((line) => line.trim());
  const headingLines = new Set(lines.filter((line, index) => line && isHeadingOrTitleLine(line, index)));
  const leadSentence = firstBodySentence(text);
  return [...text.matchAll(namePattern)].some((match) =>
    mentionIsDecedentContext(text, match, headingLines, leadSentence) && explicitMiddleNameConflict(match[0]!, packet)
  );
}

function isMiddleNameConflictField(field: string): boolean {
  return field.toLowerCase().replace(/[^a-z0-9]+/g, "").includes("middle");
}

function obituaryIdentityValues(packet: ObituaryIdentityPacket, field: ObituaryCorroboratorField): string[] {
  const values: Record<ObituaryCorroboratorField, string[]> = {
    middle_name: packet.primaryName.middle ? [packet.primaryName.middle] : [],
    suffix: packet.primaryName.suffix ? [packet.primaryName.suffix] : [],
    alias: packet.aliases,
    date_of_birth: packet.dateOfBirth ? [packet.dateOfBirth] : [],
    age: packet.age == null ? [] : [String(packet.age)],
    spouse_or_co_owner: packet.spouseOrCoOwners,
    relative: packet.relatives,
    property_address: [identityAddressDisplay(packet.propertyAddress)],
    mailing_address: packet.mailingAddress ? [identityAddressDisplay(packet.mailingAddress)] : [],
    known_location: packet.knownLocations.map(identityLocationDisplay),
    north_carolina_anchor: [],
    other: []
  };
  return values[field];
}

function equivalentPersonName(candidate: string, packet: ObituaryIdentityPacket): boolean {
  const normalizedCandidate = normalizeIdentityComparison(candidate);
  if (packet.aliases.some((alias) => normalizeIdentityComparison(alias) === normalizedCandidate)) return true;
  const candidateTokens = comparisonNameTokens(candidate);
  const first = normalizeIdentityComparison(packet.primaryName.first);
  const lastTokens = normalizeIdentityComparison(packet.primaryName.last).split(" ").filter(Boolean);
  const withoutTrailingSuffix = candidateTokens.length > 1 && NAME_SUFFIXES.has(candidateTokens.at(-1)!)
    ? candidateTokens.slice(0, -1)
    : candidateTokens;
  if (withoutTrailingSuffix.length < 2 || withoutTrailingSuffix[0] !== first) return false;
  // Middle names are optional when absent on either side, but an explicit
  // incompatible middle name is a material identity conflict. Compare the
  // complete normalized surname token sequence so Smith-Jones and Smith Jones
  // remain equivalent.
  if (!lastTokens.length || !withoutTrailingSuffix.slice(-lastTokens.length).every((token, index) => token === lastTokens[index])) return false;
  const candidateMiddle = withoutTrailingSuffix.slice(1, -lastTokens.length);
  return middleNamesCompatible(normalizedNameTokens(packet.primaryName.middle), candidateMiddle);
}

export function equivalentResolvedPersonName(left: string, right: string): boolean {
  const leftCore = nameTokensWithoutSuffix(left);
  const rightCore = nameTokensWithoutSuffix(right);
  if (leftCore.length < 2 || rightCore.length < 2) return false;
  return leftCore[0] === rightCore[0]
    && leftCore.at(-1) === rightCore.at(-1)
    && middleNamesCompatible(leftCore.slice(1, -1), rightCore.slice(1, -1));
}

function mentionsNorthCarolina(value: string): boolean {
  return /\bnorth\s+carolina\b|\bn\.?\s*c\.?\b/i.test(value);
}

function evidenceTargetMatchesPacket(packet: ObituaryIdentityPacket, evidence: ObituaryIdentityEvidence): boolean {
  if (evidence.field === "spouse_or_co_owner" || evidence.field === "relative") {
    return obituaryIdentityValues(packet, evidence.field).some((value) => equivalentResolvedPersonName(evidence.targetValue, value));
  }
  const target = normalizeIdentityComparison(evidence.targetValue);
  const allowed = obituaryIdentityValues(packet, evidence.field).map(normalizeIdentityComparison);
  return allowed.includes(target);
}

function evidenceValuesAgree(evidence: ObituaryIdentityEvidence, packetStreet = "", city = ""): boolean {
  const target = normalizeIdentityComparison(evidence.targetValue);
  const candidate = normalizeIdentityComparison(evidence.candidateValue);
  if (!target || !candidate) return false;
  if (evidence.field === "middle_name") return target === candidate || target[0] === candidate[0];
  if (evidence.field === "spouse_or_co_owner" || evidence.field === "relative") {
    return equivalentResolvedPersonName(evidence.targetValue, evidence.candidateValue);
  }
  if (evidence.field === "date_of_birth") {
    return birthDateVariants(evidence.targetValue).some((variant) => normalizeIdentityComparison(variant) === candidate);
  }
  if (evidence.field === "property_address" || evidence.field === "mailing_address") {
    return streetLevelAddressAgrees(packetStreet || evidence.targetValue, evidence.candidateValue, evidence.excerpt, city);
  }
  if (evidence.field === "known_location") {
    const targetTokens = new Set(target.split(" "));
    const candidateTokens = candidate.split(" ");
    const shared = candidateTokens.filter((token) => targetTokens.has(token));
    return shared.length >= Math.min(2, candidateTokens.length);
  }
  return target === candidate;
}

const STREET_DIRECTION_TOKENS = new Set(["n", "s", "e", "w", "north", "south", "east", "west", "ne", "nw", "se", "sw", "northeast", "northwest", "southeast", "southwest"]);
const DIRECTION_EQUIVALENTS: Record<string, readonly string[]> = {
  n: ["n", "north"],
  north: ["n", "north"],
  s: ["s", "south"],
  south: ["s", "south"],
  e: ["e", "east"],
  east: ["e", "east"],
  w: ["w", "west"],
  west: ["w", "west"],
  ne: ["ne", "northeast"],
  northeast: ["ne", "northeast"],
  nw: ["nw", "northwest"],
  northwest: ["nw", "northwest"],
  se: ["se", "southeast"],
  southeast: ["se", "southeast"],
  sw: ["sw", "southwest"],
  southwest: ["sw", "southwest"]
};
const STREET_DESIGNATOR_FAMILIES: readonly string[][] = [
  ["street", "st"],
  ["road", "rd"],
  ["avenue", "ave"],
  ["lane", "ln"],
  ["drive", "dr"],
  ["court", "ct"],
  ["boulevard", "blvd"],
  ["highway", "hwy"],
  ["circle", "cir"],
  ["place", "pl"],
  ["parkway", "pkwy"],
  ["trail", "trl"],
  ["way"],
  ["terrace", "ter"]
];
const PERSON_NAME_STOPWORDS = new Set([
  "helped", "recalled", "how", "and", "the", "of", "by", "is", "was", "her", "his", "their",
  "survived", "passed", "died", "cared", "for", "with", "from", "who", "whose", "after"
]);
const RELATIVE_ROLE_PATTERN = /\b(?:mother|father|wife|husband|son|daughter|brother|sister|grandmother|grandfather|parent|cousin|niece|nephew|grandchild|grandson|granddaughter|aunt|uncle)s?\b/i;
const SPOUSE_RELATION_PATTERN = /\b(?:wife|husband|spouse|widow|widower|co-?owner)\b|\b(?:survived|preceded in death|predeceased) by\b/i;
const FAMILY_RELATION_PATTERN = /\b(?:son|daughter|child|children|mother|father|parent|brother|sister|sibling|grandson|granddaughter|grandchild|niece|nephew|cousin|aunt|uncle)\b|\b(?:survived|preceded in death|predeceased) by\b/i;
const COORDINATED_SPAN_WORDS = new Set([
  "and", "or", "of", "all", "the", "a", "an", "her", "his", "their", "age", "years", "old",
  "formerly", "late", "both", "each", "nee", "mr", "mrs", "ms", "dr", "rev", "prof", "hon", "pastor",
  "husband", "wife", "spouse", "widow", "widower", "son", "sons", "daughter", "daughters",
  "child", "children", "mother", "father", "parent", "parents", "brother", "brothers", "sister",
  "sisters", "sibling", "siblings", "cousin", "cousins", "niece", "nephew", "grandson",
  "granddaughter", "grandchild", "grandchildren", "aunt", "uncle"
]);

function mergeAdjacentDirectionTokens(tokens: string[]): string[] {
  const merged: string[] = [];
  for (let index = 0; index < tokens.length; index += 1) {
    const current = tokens[index]!;
    const next = tokens[index + 1];
    if (next && ["n", "s"].includes(current) && ["e", "w"].includes(next)) {
      merged.push(`${current}${next}`);
      index += 1;
      continue;
    }
    merged.push(current);
  }
  return merged;
}

function parseStreetParts(street: string): { number: string; names: string[]; family: string[]; suffix: string[] } | null {
  const tokens = mergeAdjacentDirectionTokens(normalizeIdentityComparison(street).split(" ").filter(Boolean));
  const numberIndex = tokens.findIndex((token) => /^\d+[a-z]?$/.test(token));
  if (numberIndex < 0) return null;
  const number = tokens[numberIndex]!;
  const after = tokens.slice(numberIndex + 1);
  while (after.length >= 2 && /^(?:apt|unit|ste|suite|#)$/.test(after.at(-2)!) && /^(?:\d+[a-z]?|#)$/.test(after.at(-1)!)) {
    after.pop();
    after.pop();
  }
  while (after.length && /^(?:apt|unit|ste|suite|#)$/.test(after.at(-1)!)) after.pop();
  const suffix: string[] = [];
  while (after.length && STREET_DIRECTION_TOKENS.has(after.at(-1)!)) suffix.unshift(after.pop()!);
  if (!after.length) return null;
  const last = after.at(-1)!;
  const family = STREET_DESIGNATOR_FAMILIES.find((group) => group.includes(last)) ?? null;
  if (!family) {
    return { number, names: after, family: [], suffix };
  }
  const names = after.slice(0, -1);
  if (!names.length) return null;
  return { number, names, family, suffix };
}

function escapeIdentityRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function directionTokenPattern(token: string): string {
  const alternatives = DIRECTION_EQUIVALENTS[token] ?? [token];
  const compact = alternatives.find((value) => value.length <= 2) ?? token;
  const letters = compact.replace(/[^a-z]/g, "");
  const longForm = alternatives.find((value) => value.length > 2);
  let body: string;
  if (letters.length === 2) {
    const first = escapeIdentityRegExp(letters[0]!);
    const second = escapeIdentityRegExp(letters[1]!);
    body = `${first}\\.?\\s*${second}\\.?|${escapeIdentityRegExp(letters)}${longForm ? `|${escapeIdentityRegExp(longForm)}` : ""}`;
  } else if (letters.length === 1) {
    body = `${escapeIdentityRegExp(letters)}\\.?${longForm ? `|${escapeIdentityRegExp(longForm)}` : ""}`;
  } else {
    body = alternatives.map((value) => escapeIdentityRegExp(value)).join("|");
  }
  return `(?:${body})(?![.\\s]*[nesw]\\b)`;
}

function streetNameTokenPattern(token: string, index: number): string {
  if (index === 0 && STREET_DIRECTION_TOKENS.has(token)) return directionTokenPattern(token);
  if (token.length <= 2) return `${escapeIdentityRegExp(token)}\\.?`;
  return escapeIdentityRegExp(token);
}

function streetAddressPhrasePattern(street: string): RegExp | null {
  const parsed = parseStreetParts(street);
  if (!parsed) return null;
  const nameSeq = parsed.names.map((token, index) => streetNameTokenPattern(token, index)).join("[\\s,]+");
  const designator = parsed.family.length
    ? `[\\s,]+(?:${parsed.family.map((token) => escapeIdentityRegExp(token)).join("|")})\\.?`
    : "";
  const suffix = parsed.suffix.length
    ? `[\\s,]+${parsed.suffix.map((token) => directionTokenPattern(token)).join("[\\s,]+")}`
    : "";
  return new RegExp(
    `\\b${escapeIdentityRegExp(parsed.number)}\\b[\\s,]+${nameSeq}${designator}${suffix}\\b`,
    "i"
  );
}

function followingLocalityConflicts(sourceText: string, matchIndex: number, matchLength: number, city: string): boolean {
  const cityNorm = normalizeIdentityComparison(city);
  if (!cityNorm) return false;
  const following = sourceText.slice(matchIndex + matchLength, matchIndex + matchLength + 64);
  const followNorm = normalizeIdentityComparison(following);
  if (followNorm.includes(cityNorm)) return false;
  const mentioned = following.match(/,\s*([A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)?)\b/);
  if (!mentioned) return false;
  const locality = normalizeIdentityComparison(mentioned[1]!);
  if (locality === "north carolina" || locality === "nc") return false;
  return locality !== cityNorm;
}

function sourceHasStreetAddressPhrase(street: string, sourceText: string, city = ""): boolean {
  return Boolean(packetStreetExcerpt(street, city, sourceText));
}

function packetStreetExcerpt(street: string, city: string, sourceText: string): string | null {
  const pattern = streetAddressPhrasePattern(street);
  if (!pattern) return null;
  const global = new RegExp(pattern.source, "gi");
  for (const match of sourceText.matchAll(global)) {
    if (match.index == null) continue;
    if (followingLocalityConflicts(sourceText, match.index, match[0].length, city)) continue;
    if (streetMatchIsServiceVenue(sourceText, match.index)) continue;
    const excerpt = sourceText.slice(Math.max(0, match.index - 80), Math.min(sourceText.length, match.index + match[0].length + 160)).trim();
    if (excerpt) return excerpt;
  }
  return null;
}

// ponytail: nearest preposition/noun-phrase venue skip, not full discourse attribution; upgrade: bind street to a decedent clause like age/DOB.
const STREET_SERVICE_VENUE_CUE = /(?:\b(?:funeral\s+home|funeral\s+chapel|mortuary|services?(?:\s+will\s+be)?(?:\s+held)?\s+at|in\s+lieu\s+of\s+flowers|donations?|memorials?(?:\s+may\s+be)?\s+(?:sent|made)|interment|visitation|burial|graveside|entombment|cemetery|memorial\s+gardens|celebration\s+of\s+life)\b|\bchapel,)/i;
const STREET_RESIDENCE_CUE = /\b(?:at\s+(?:her|his|their)\s+(?:home|residence)|resided\s+at|lived\s+at)\s*,?\s*$/i;
const STREET_VENUE_HEAD = String.raw`(?:funeral\s+home|chapel|mortuary|hospice|hospital|church|cemetery|donations?|memorials?)`;
const STREET_VENUE_HEAD_OF = new RegExp(`\\b${STREET_VENUE_HEAD}\\s+of\\s+$`, "i");
const STREET_VENUE_HEAD_COMMA = new RegExp(`\\b${STREET_VENUE_HEAD}\\s*,\\s*$`, "i");

function streetMatchIsServiceVenue(sourceText: string, matchIndex: number): boolean {
  const { start } = sentenceAt(sourceText, matchIndex);
  const prefix = sourceText.slice(start, matchIndex);
  if (STREET_RESIDENCE_CUE.test(prefix)) return false;
  if (/\bof\s+$/i.test(prefix)) return STREET_VENUE_HEAD_OF.test(prefix);
  if (STREET_VENUE_HEAD_COMMA.test(prefix)) return true;
  return STREET_SERVICE_VENUE_CUE.test(prefix);
}

function packetAddressParts(packet: ObituaryIdentityPacket, field: "property_address" | "mailing_address"): { street: string; city: string } {
  const address = field === "mailing_address" ? packet.mailingAddress : packet.propertyAddress;
  return { street: address?.street ?? "", city: address?.city ?? "" };
}

function streetLevelAddressAgrees(street: string, candidate: string, excerpt: string, city = ""): boolean {
  return sourceHasStreetAddressPhrase(street, `${candidate} ${excerpt}`, city);
}

function excerptAround(text: string, pattern: RegExp): string | null {
  const match = pattern.exec(text);
  if (!match || match.index == null) return null;
  return text.slice(Math.max(0, match.index - 80), Math.min(text.length, match.index + match[0].length + 160)).trim() || null;
}

function caseInsensitiveTokenPattern(token: string): string {
  return escapeIdentityRegExp(token).replace(/[a-z]/gi, (letter) => `[${letter.toLowerCase()}${letter.toUpperCase()}]`);
}

function personNameMatches(text: string, value: string): Array<{ excerpt: string; observed: string; index: number }> {
  const tokens = nameTokensWithoutSuffix(value);
  if (tokens.length < 2) return [];
  const first = caseInsensitiveTokenPattern(tokens[0]!);
  const last = caseInsensitiveTokenPattern(tokens.at(-1)!);
  const pattern = new RegExp(`\\b(${first}(?:[ \\t]+[A-Z](?:[a-zA-Z]+|\\.)?){0,2}[ \\t'’-]+${last})\\b`, "g");
  const matches: Array<{ excerpt: string; observed: string; index: number }> = [];
  for (const match of text.matchAll(pattern)) {
    const observed = match[1]?.replace(/\s+/g, " ").trim();
    if (!observed || match.index == null || !equivalentResolvedPersonName(value, observed)) continue;
    const middleTokens = nameTokensWithoutSuffix(observed).slice(1, -1);
    if (middleTokens.some((token) => PERSON_NAME_STOPWORDS.has(token))) continue;
    const excerpt = text.slice(Math.max(0, match.index - 80), Math.min(text.length, match.index + match[0].length + 160)).trim();
    if (excerpt) matches.push({ excerpt, observed, index: match.index });
  }
  return matches;
}

function relationshipCueAttaches(between: string): boolean {
  const trimmed = between.trim();
  if (!trimmed) return true;
  const tokens = trimmed.match(/[A-Za-z]+|[0-9]+/g) ?? [];
  if (!tokens.length) return true;
  for (const token of tokens) {
    if (/^[A-Z]/.test(token) || /^\d+$/.test(token)) continue;
    const lower = token.toLowerCase();
    if (COORDINATED_SPAN_WORDS.has(lower) || NAME_SUFFIXES.has(lower)) continue;
    return false;
  }
  return true;
}

function familyNameWithRelationship(
  text: string,
  value: string,
  field: "spouse_or_co_owner" | "relative"
): { excerpt: string; observed: string } | null {
  const cue = field === "spouse_or_co_owner" ? SPOUSE_RELATION_PATTERN : FAMILY_RELATION_PATTERN;
  for (const match of personNameMatches(text, value)) {
    const { start, sentence } = sentenceAt(text, match.index);
    const before = sentence.slice(0, Math.max(0, match.index - start));
    const after = sentence.slice(Math.max(0, match.index - start + match.observed.length));
    const attached = [...before.matchAll(new RegExp(cue.source, "gi"))].at(-1);
    const following = /^(?:[, ]+(?:her|his|their)\s+(?:husband|wife|spouse|widow|widower|son|daughter|child|mother|father|brother|sister|cousin|niece|nephew))\b/i.test(after);
    if (following) return match;
    if (attached?.index == null) continue;
    const between = before.slice(attached.index + attached[0].length);
    if (field === "spouse_or_co_owner" && /\b(?:sons?|daughters?|children|child|mother|father|brother|sister|cousin|niece|nephew|grandchild|grandson|granddaughter|aunt|uncle)s?\b/i.test(between)) continue;
    if (!relationshipCueAttaches(between)) continue;
    return match;
  }
  return null;
}

function ageFactPattern(age: number | string, flags = "gi"): RegExp {
  const value = escapeIdentityRegExp(String(age));
  return new RegExp(`(?:\\bage\\s+${value}(?!\\d)|(?<!\\d)${value}\\s+years?\\s+old|(?<!\\d),\\s*${value}\\s*,)`, flags);
}

function primaryNameTokenSet(packet: ObituaryIdentityPacket): Set<string> {
  return new Set([
    ...normalizedNameTokens(packet.primaryName.first),
    ...normalizedNameTokens(packet.primaryName.middle),
    ...normalizedNameTokens(packet.primaryName.last),
    ...normalizedNameTokens(packet.primaryName.suffix)
  ]);
}

function aliasIsIndependent(alias: string, packet: ObituaryIdentityPacket): boolean {
  const tokens = normalizedNameTokens(alias);
  if (!tokens.length) return false;
  const nameTokens = primaryNameTokenSet(packet);
  return tokens.some((token) => !nameTokens.has(token));
}

function isNameInitialPeriod(text: string, periodIndex: number): boolean {
  const prev = text[periodIndex - 1] ?? "";
  const prev2 = text[periodIndex - 2] ?? "";
  const next = text[periodIndex + 1] ?? "";
  if (/[A-Za-z]/.test(prev) && !/[A-Za-z]/.test(prev2) && /[\s,]/.test(next)) return true;
  const before = text.slice(Math.max(0, periodIndex - 5), periodIndex + 1);
  return /(?:^|[^A-Za-z])(?:Mr|Mrs|Ms|Dr|Jr|Sr|St|Rev|Prof|Hon|vs|etc)\.$/i.test(before) && /[\s,]/.test(next);
}

function sentenceAt(text: string, index: number): { start: number; sentence: string } {
  let start = 0;
  for (let i = index - 1; i >= 0; i -= 1) {
    const ch = text[i]!;
    if (ch === "\n" || ch === "!" || ch === "?") { start = i + 1; break; }
    if (ch === "." && !isNameInitialPeriod(text, i)) { start = i + 1; break; }
  }
  let end = text.length;
  for (let i = index; i < text.length; i += 1) {
    const ch = text[i]!;
    if (ch === "\n" || ch === "!" || ch === "?") { end = i; break; }
    if (ch === "." && !isNameInitialPeriod(text, i)) { end = i; break; }
  }
  return { start, sentence: text.slice(start, end) };
}

function attributedAliasMatch(text: string, alias: string, packet: ObituaryIdentityPacket): { excerpt: string; observed: string } | null {
  const escaped = escapeIdentityRegExp(alias.trim());
  if (!escaped) return null;
  const pattern = new RegExp(
    `["“”'](${escaped})["“”']|\\((${escaped})\\)|\\b(?:a\\.?k\\.?a\\.?|also known as|known as|nicknamed|nickname)\\s+["“”']?(${escaped})["“”']?\\b`,
    "i"
  );
  const match = pattern.exec(text);
  const observed = (match?.[1] || match?.[2] || match?.[3] || "").trim();
  if (!observed || match?.index == null) return null;
  const { start } = sentenceAt(text, match.index);
  const first = escapeIdentityRegExp(packet.primaryName.first.trim());
  const last = escapeIdentityRegExp(packet.primaryName.last.trim());
  const window = text.slice(Math.max(0, match.index - 48), match.index + match[0].length + 48);
  const wrapped = new RegExp(`\\b${first}\\b[\\s,]*["“”'(]?${escaped}["“”')]?[\\s,]*\\b${last}\\b`, "i");
  const prefix = text.slice(start, match.index).replace(/[,:\s]+$/, "");
  const named = new RegExp(`\\b${first}\\b(?:\\s+[A-Z][A-Za-z.]*){0,2}\\s+${last}$`, "i");
  if (RELATIVE_ROLE_PATTERN.test(prefix) || SURVIVOR_CLAUSE_PREFIX.test(prefix)) return null;
  const snippet = text.slice(Math.max(0, match.index - 40), match.index + match[0].length + 24);
  if (aliasSuffixConflictsPacket(snippet, packet)) return null;
  if (!wrapped.test(window) && !named.test(prefix.trim())) return null;
  const excerpt = excerptAround(text, pattern);
  return excerpt ? { excerpt, observed } : null;
}

function aliasSuffixConflictsPacket(snippet: string, packet: ObituaryIdentityPacket): boolean {
  const found = snippet.match(/\b(jr|junior|sr|senior|ii|iii|iv)\b/i)?.[1];
  if (!found) return false;
  const packetSuffix = normalizeIdentityComparison(packet.primaryName.suffix);
  if (!packetSuffix) return false;
  const mention = normalizeIdentityComparison(found);
  const junior = new Set(["jr", "junior"]);
  const senior = new Set(["sr", "senior"]);
  if (junior.has(packetSuffix) && senior.has(mention)) return true;
  if (senior.has(packetSuffix) && junior.has(mention)) return true;
  return packetSuffix !== mention && !(junior.has(packetSuffix) && junior.has(mention)) && !(senior.has(packetSuffix) && senior.has(mention));
}

function factAttributedToDecedent(text: string, factIndex: number, packet: ObituaryIdentityPacket): boolean {
  const { start, sentence } = sentenceAt(text, factIndex);
  if (SURVIVOR_CLAUSE_PREFIX.test(sentence)) return false;
  const before = sentence.slice(0, Math.max(0, factIndex - start));
  if (RELATIVE_ROLE_PATTERN.test(before)) return false;
  const first = escapeIdentityRegExp(packet.primaryName.first.trim());
  const last = escapeIdentityRegExp(packet.primaryName.last.trim());
  const clause = (before.split(/\s+and\s+|;\s*/).pop() ?? before).replace(/[,:\s]+$/, "");
  if (!new RegExp(`\\b${first}\\b(?:\\s+[A-Z][A-Za-z.]*){0,2}\\s+${last}(?:[,\\s]+(?:jr|junior|sr|senior|ii|iii|iv)\\.?)?(?:[,\\s]+(?:was(?:\\s+born(?:\\s+on)?)?|born(?:\\s+on)?))?$`, "i").test(clause.trim())) return false;
  return !aliasSuffixConflictsPacket(clause, packet);
}

function birthFactAttributedToDecedent(text: string, factIndex: number, packet: ObituaryIdentityPacket): boolean {
  if (!factAttributedToDecedent(text, factIndex, packet)) return false;
  return /\bborn\b|\bbirth\b|\bdate of birth\b|\bdob\b/i.test(sentenceAt(text, factIndex).sentence);
}

function birthDateVariants(iso: string): string[] {
  const [year, month, day] = iso.split("-");
  if (!year || !month || !day) return [];
  const monthNum = Number(month);
  const dayNum = Number(day);
  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const monthName = monthNames[monthNum - 1];
  return [
    iso,
    `${month}/${day}/${year}`,
    `${monthNum}/${day}/${year}`,
    `${month}/${dayNum}/${year}`,
    `${monthNum}/${dayNum}/${year}`,
    ...(monthName ? [`${monthName} ${dayNum}, ${year}`, `${monthName} ${day}, ${year}`] : [])
  ];
}

function observedBirthDate(text: string, iso: string, packet: ObituaryIdentityPacket): { excerpt: string; observed: string } | null {
  for (const variant of birthDateVariants(iso)) {
    const pattern = new RegExp(`(?<!\\d)${escapeIdentityRegExp(variant)}(?!\\d)`, "gi");
    for (const match of text.matchAll(pattern)) {
      if (match.index == null || !birthFactAttributedToDecedent(text, match.index, packet)) continue;
      const excerpt = excerptAround(text, new RegExp(pattern.source, "i"));
      if (excerpt) return { excerpt, observed: match[0]! };
    }
  }
  return null;
}

function corroboratingValueInSource(
  packet: ObituaryIdentityPacket,
  evidence: ObituaryIdentityEvidence,
  text: string
): boolean {
  if (evidence.field === "property_address" || evidence.field === "mailing_address") {
    const { street, city } = packetAddressParts(packet, evidence.field);
    return sourceHasStreetAddressPhrase(street, text, city);
  }
  if (evidence.field === "age") {
    const pattern = ageFactPattern(packet.age ?? evidence.candidateValue);
    return [...text.matchAll(pattern)].some((match) => match.index != null && factAttributedToDecedent(text, match.index, packet));
  }
  if (evidence.field === "date_of_birth") {
    return Boolean(packet.dateOfBirth && observedBirthDate(text, packet.dateOfBirth, packet));
  }
  const value = normalizeIdentityComparison(evidence.candidateValue);
  return Boolean(value) && normalizeIdentityComparison(text).includes(value);
}

function independentPersonSpecificEvidence(
  packet: ObituaryIdentityPacket,
  evidence: ObituaryIdentityEvidence,
  text: string
): boolean {
  if (!isPersonSpecificCorroboratorField(evidence.field)) return true;
  if (!corroboratingValueInSource(packet, evidence, text)) return false;
  if (evidence.field === "alias") {
    return aliasIsIndependent(evidence.targetValue, packet) && Boolean(attributedAliasMatch(text, evidence.targetValue, packet));
  }
  if (evidence.field === "spouse_or_co_owner" || evidence.field === "relative") {
    return Boolean(familyNameWithRelationship(text, evidence.candidateValue, evidence.field));
  }
  return true;
}

export function isPersonSpecificCorroboratorField(field: string): field is PersonSpecificCorroboratorField {
  return (PERSON_SPECIFIC_CORROBORATOR_FIELDS as readonly string[]).includes(field);
}

/**
 * Ground packet owner facts in selected source text. This is not a new matcher:
 * it only records facts already on the identity packet when the selected
 * obituary quotes them.
 */
export function collectGroundedObituaryCorroborators(
  packetInput: ObituaryIdentityPacket,
  candidateTextById: Record<string, string>,
  selectedIds: Iterable<string>
): ObituaryIdentityEvidence[] {
  const packet = ObituaryIdentityPacketSchema.parse(packetInput);
  const grounded: ObituaryIdentityEvidence[] = [];
  for (const candidateId of selectedIds) {
    const text = candidateTextById[candidateId];
    if (!text) continue;
    const push = (field: ObituaryCorroboratorField, targetValue: string, excerpt: string, candidateValue = excerpt.slice(0, 500)) => {
      grounded.push({ field, targetValue, candidateValue: candidateValue.slice(0, 500), candidateId, excerpt: excerpt.slice(0, 1_200) });
    };
    for (const spouse of packet.spouseOrCoOwners) {
      const match = familyNameWithRelationship(text, spouse, "spouse_or_co_owner");
      if (match) push("spouse_or_co_owner", spouse, match.excerpt, match.observed);
    }
    for (const relative of packet.relatives) {
      const match = familyNameWithRelationship(text, relative, "relative");
      if (match) push("relative", relative, match.excerpt, match.observed);
    }
    for (const alias of packet.aliases) {
      if (!aliasIsIndependent(alias, packet)) continue;
      const match = attributedAliasMatch(text, alias, packet);
      if (match) push("alias", alias, match.excerpt, match.observed);
    }
    if (packet.propertyAddress.street.trim()) {
      const excerpt = packetStreetExcerpt(packet.propertyAddress.street, packet.propertyAddress.city, text);
      if (excerpt) push("property_address", identityAddressDisplay(packet.propertyAddress), excerpt);
    }
    if (packet.mailingAddress?.street.trim()) {
      const excerpt = packetStreetExcerpt(packet.mailingAddress.street, packet.mailingAddress.city, text);
      if (excerpt) push("mailing_address", identityAddressDisplay(packet.mailingAddress), excerpt);
    }
    if (packet.dateOfBirth) {
      const match = observedBirthDate(text, packet.dateOfBirth, packet);
      if (match) push("date_of_birth", packet.dateOfBirth, match.excerpt, match.observed);
    }
    if (packet.age != null) {
      const pattern = ageFactPattern(packet.age);
      for (const match of text.matchAll(pattern)) {
        if (match.index == null || !factAttributedToDecedent(text, match.index, packet)) continue;
        const excerpt = excerptAround(text, new RegExp(pattern.source, "i"));
        if (excerpt) {
          push("age", String(packet.age), excerpt, String(packet.age));
          break;
        }
      }
    }
  }
  return grounded;
}

function capRawCorroborators(items: unknown[]): unknown[] {
  const seen = new Set<string>();
  const anchors: unknown[] = [];
  const rest: unknown[] = [];
  for (const item of items) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const field = String(record.field ?? "");
    const candidateId = String(record.candidateId ?? record.candidate_id ?? "");
    const targetValue = String(record.targetValue ?? record.target_value ?? "");
    const key = `${field}|${candidateId}|${normalizeIdentityComparison(targetValue)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    (field === "north_carolina_anchor" ? anchors : rest).push(item);
  }
  return [...anchors, ...rest].slice(0, 50);
}

function resolutionWithCappedCorroborators(input: unknown): unknown {
  if (!input || typeof input !== "object" || !("corroborators" in input) || !Array.isArray(input.corroborators)) return input;
  return { ...input, corroborators: capRawCorroborators(input.corroborators) };
}

function mergeObituaryCorroborators(submitted: ObituaryIdentityEvidence[], grounded: ObituaryIdentityEvidence[]): ObituaryIdentityEvidence[] {
  return capRawCorroborators([...grounded, ...submitted]) as ObituaryIdentityEvidence[];
}

function rejectedCorroboratorReason(
  evidence: ObituaryIdentityEvidence,
  packet: ObituaryIdentityPacket,
  selected: Set<string>,
  candidateTextById: Record<string, string>
): string {
  const text = candidateTextById[evidence.candidateId] ?? "";
  const excerptGrounded = Boolean(text) && normalizeIdentityComparison(text).includes(normalizeIdentityComparison(evidence.excerpt));
  if (!selected.has(evidence.candidateId)) return "Corroborator was not on a selected candidate.";
  if (!excerptGrounded) return "Excerpt was not grounded in selected source text.";
  if (evidence.field === "north_carolina_anchor") return "State reference is required locality evidence but is not independent person-specific corroboration.";
  if (evidence.field === "known_location") return "City or state location is not independent person-specific corroboration.";
  if (evidence.field === "middle_name" || evidence.field === "suffix") return "Name components are not independent of the decedent-name rule.";
  if (evidence.field === "other") return "Field other is not a typed owner-fact corroborator.";
  if (evidence.field === "property_address" || evidence.field === "mailing_address") {
    const { street, city } = packetAddressParts(packet, evidence.field);
    if (!sourceHasStreetAddressPhrase(street, text, city)) {
      return "Address corroboration requires a street-address phrase in selected source text, with a compatible designator.";
    }
  }
  if (evidence.field === "alias") {
    if (!aliasIsIndependent(evidence.targetValue, packet)) return "Alias is not distinct from the decedent given name.";
    return "Alias must be an attributed nickname with token boundaries in selected source text.";
  }
  if (evidence.field === "age" || evidence.field === "date_of_birth") {
    return "Age or birth date must be attributed to the decedent, not a relative.";
  }
  if (evidence.field === "spouse_or_co_owner" || evidence.field === "relative") {
    return "Observed source name must be a person-name phrase equivalent to the owner fact.";
  }
  const packetAddress = packetAddressParts(packet, evidence.field === "mailing_address" ? "mailing_address" : "property_address");
  if (!evidenceTargetMatchesPacket(packet, evidence) || !evidenceValuesAgree(evidence, packetAddress.street, packetAddress.city)) {
    return "Candidate value did not match the submitted owner fact.";
  }
  return "Corroborator did not satisfy the independent person-specific rule.";
}

function explainObituaryIdentityAcceptance(input: {
  decision: "confirmed_match" | "ambiguous_review" | "no_match";
  personSpecific: ObituaryIdentityEvidence[];
  valid: ObituaryIdentityEvidence[];
  rejected: ObituaryIdentityRejectedCorroborator[];
  assessments: ObituaryCandidateAssessment[];
  reviewReasons: string[];
}): string {
  const satisfied = input.personSpecific.length
    ? input.personSpecific.map((evidence) => `${evidence.field} (${evidence.candidateValue})`).join("; ")
    : "none";
  const alternatives = input.assessments
    .filter((assessment) => assessment.disposition !== "selected_same_person")
    .map((assessment) => `${assessment.candidateId} ${assessment.disposition}: ${assessment.rationale}`)
    .slice(0, 6);
  const alternativeText = alternatives.length
    ? ` Rejected alternatives: ${alternatives.join(" ")}`
    : " No competing selected alternative remained.";
  if (input.decision === "confirmed_match") {
    return `Confirmed because independent person-specific corroborators matched owner facts in selected source text: ${satisfied}. North Carolina is required locality evidence and is not sufficient alone.${alternativeText}`;
  }
  if (input.reviewReasons.includes(OBITUARY_NAME_PLUS_STATE_INSUFFICIENT_REASON)) {
    const grounded = input.valid.map((evidence) => evidence.field).join(", ") || "none";
    return `${OBITUARY_NAME_PLUS_STATE_INSUFFICIENT_REASON} Grounded corroborators were ${grounded}.${alternativeText}`;
  }
  return `${input.decision === "ambiguous_review" ? "Not confirmed because competing candidates or material conflicts remain." : "Not confirmed."} ${input.reviewReasons.join(" ")}${alternativeText}`.trim();
}

function normalizedExplicitDeathDates(text: string): string[] {
  const months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
  const normalize = (value: string): string | null => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const numeric = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (numeric) {
      const month = Number(numeric[1]);
      const day = Number(numeric[2]);
      return month >= 1 && month <= 12 && day >= 1 && day <= 31
        ? `${numeric[3]}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
        : null;
    }
    const named = /^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/.exec(value);
    if (!named) return null;
    const month = months.indexOf(named[1]!.toLowerCase()) + 1;
    const day = Number(named[2]);
    return month >= 1 && day >= 1 && day <= 31
      ? `${named[3]}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`
      : null;
  };
  const datePattern = "(?:[A-Za-z]+\\s+\\d{1,2},\\s*\\d{4}|\\d{1,2}\\/\\d{1,2}\\/\\d{4}|\\d{4}-\\d{2}-\\d{2})";
  const marked = new RegExp(`\\b(?:died|passed away|departed this life|death(?: date)?(?: was|:))\\s+(?:on\\s+)?(${datePattern})`, "ig");
  const values = [...text.matchAll(marked)].map((match) => normalize(match[1]!)).filter((value): value is string => value !== null);
  if (values.length) return [...new Set(values)];
  const title = text.split("\n", 1)[0] ?? "";
  return [...new Set([...title.matchAll(new RegExp(`\\b(${datePattern})\\b`, "ig"))]
    .map((match) => normalize(match[1]!))
    .filter((value): value is string => value !== null))];
}

/**
 * Deterministic postcondition for V3 candidate matches. The model groups
 * duplicate publisher pages, but cannot waive exact first/last-name evidence,
 * a quoted North Carolina anchor, independent person-specific corroboration
 * matched to owner facts, or a material conflict on the selected /
 * still-plausible person set. Name plus a state reference cannot confirm.
 *
 * Rejected pages (`different_person` / `insufficient_evidence`) may still
 * carry material “this is someone else” notes. Those notes are why the page
 * was rejected; they must not poison a clean selected match.
 */
export function verifyObituaryIdentityResolution(
  packetInput: ObituaryIdentityPacket,
  candidateTextById: Record<string, string>,
  resolutionInput: unknown
): VerifiedObituaryIdentityResolution {
  const packet = ObituaryIdentityPacketSchema.parse(packetInput);
  const parsedResolution = ObituaryIdentityResolutionSchema.parse(resolutionWithCappedCorroborators(resolutionInput));
  const candidateIds = Object.keys(candidateTextById);
  const reviewReasons: string[] = [];
  const assessedIds = parsedResolution.candidateAssessments.map((assessment) => assessment.candidateId);
  if (assessedIds.length !== candidateIds.length || new Set(assessedIds).size !== candidateIds.length || candidateIds.some((id) => !assessedIds.includes(id))) {
    reviewReasons.push("AI assessment did not cover every retained obituary candidate exactly once.");
  }

  // Recover a singleton selection when the model marked exactly one page as
  // selected_same_person but left selectedCandidateIds empty. Live backfill
  // runs hit this shape often enough that it was creating false ambiguous
  // reviews on otherwise source-backed matches.
  const selectedFromAssessments = parsedResolution.candidateAssessments
    .filter((assessment) => assessment.disposition === "selected_same_person")
    .map((assessment) => assessment.candidateId);
  const recoveredSelectedIds = parsedResolution.selectedCandidateIds.length
    ? parsedResolution.selectedCandidateIds
    : selectedFromAssessments.length === 1
      ? selectedFromAssessments
      : [];
  const selected = new Set(recoveredSelectedIds);
  if (selected.size !== recoveredSelectedIds.length || [...selected].some((id) => !candidateIds.includes(id))) {
    reviewReasons.push("Selected obituary candidate IDs were missing, duplicated, or unknown.");
  }
  const selectedAssessments = parsedResolution.candidateAssessments.filter((assessment) => selected.has(assessment.candidateId));
  const singletonSelected = selected.size === 1
    && selectedAssessments.length === 1
    && selectedAssessments[0]?.disposition === "selected_same_person";
  // A singleton selected page may omit entityId; synthesize a stable group id
  // so the grouping check does not fail on bookkeeping alone.
  const effectiveEntityId = parsedResolution.selectedEntityId
    ?? (singletonSelected
      ? (selectedAssessments[0]?.entityId ?? selectedAssessments[0]?.candidateId ?? null)
      : null);
  const groundedCorroborators = collectGroundedObituaryCorroborators(packet, candidateTextById, selected);
  const mergedCorroborators = mergeObituaryCorroborators(parsedResolution.corroborators, groundedCorroborators);
  const resolution: ObituaryIdentityResolution = {
    ...parsedResolution,
    selectedCandidateIds: recoveredSelectedIds,
    selectedEntityId: effectiveEntityId,
    corroborators: mergedCorroborators
  };
  if (!selected.size || !effectiveEntityId || selectedAssessments.length !== selected.size || selectedAssessments.some((assessment) =>
    assessment.disposition !== "selected_same_person"
    || (assessment.entityId != null && assessment.entityId !== effectiveEntityId)
    || (assessment.entityId == null && !singletonSelected)
  )) {
    reviewReasons.push("The selected pages were not all grouped as the same uniquely identified person.");
  }
  for (const assessment of selectedAssessments) {
    const text = candidateTextById[assessment.candidateId] ?? "";
    const evidence = assessment.nameEvidence;
    const middleConflict = explicitMiddleNameConflict(assessment.decedentName ?? "", packet)
      || explicitMiddleNameConflict(evidence?.candidateValue ?? "", packet)
      || candidateHeadingHasMiddleConflict(text, packet);
    if (middleConflict) reviewReasons.push(`Candidate ${assessment.candidateId} has a material middle-name conflict with the submitted owner.`);
    if (!evidence || !assessment.decedentName || !equivalentPersonName(assessment.decedentName, packet) ||
      !equivalentPersonName(evidence.targetValue, packet) ||
      !equivalentPersonName(evidence.candidateValue, packet) ||
      !normalizeIdentityComparison(text).includes(normalizeIdentityComparison(evidence.excerpt)) ||
      middleConflict) {
      reviewReasons.push(`Candidate ${assessment.candidateId} lacks grounded full-name evidence for the target person.`);
    }
  }
  const selectedDeathDates = [...new Set(selectedAssessments.flatMap((assessment) => assessment.deathDate
    ? [assessment.deathDate]
    : normalizedExplicitDeathDates(candidateTextById[assessment.candidateId] ?? "")))];
  const hasDeterministicCandidateConflict = selectedDeathDates.length > 1;
  if (hasDeterministicCandidateConflict) {
    reviewReasons.push("Selected same-name obituary candidates contain different explicit death dates and cannot be grouped as one person.");
  }
  if (resolution.decedentName && !equivalentPersonName(resolution.decedentName, packet)) {
    reviewReasons.push("Top-level decedent name does not match the submitted owner identity.");
  }
  if (resolution.decedentName && selectedAssessments.some((assessment) => assessment.decedentName && !equivalentResolvedPersonName(resolution.decedentName!, assessment.decedentName))) {
    reviewReasons.push("Top-level decedent name is not bound to every selected candidate assessment.");
  }
  if (selectedDeathDates.length && (!resolution.deathDate || !selectedDeathDates.includes(resolution.deathDate))) {
    reviewReasons.push("Top-level death date is missing or does not match the selected candidate evidence.");
  }
  const validCorroborators = resolution.corroborators.filter((evidence) => {
    const text = candidateTextById[evidence.candidateId] ?? "";
    const excerptGrounded = normalizeIdentityComparison(text).includes(normalizeIdentityComparison(evidence.excerpt));
    if (!selected.has(evidence.candidateId) || !excerptGrounded) return false;
    if (evidence.field === "north_carolina_anchor") {
      return mentionsNorthCarolina(evidence.targetValue) &&
        (mentionsNorthCarolina(evidence.candidateValue) || mentionsNorthCarolina(evidence.excerpt));
    }
    const packetAddress = packetAddressParts(packet, evidence.field === "mailing_address" ? "mailing_address" : "property_address");
    return evidence.field !== "other"
      && evidenceTargetMatchesPacket(packet, evidence)
      && evidenceValuesAgree(evidence, packetAddress.street, packetAddress.city)
      && independentPersonSpecificEvidence(packet, evidence, text);
  });
  const corroboratingFields = [...new Set(validCorroborators.map((evidence) => evidence.field))];
  if (!corroboratingFields.includes("north_carolina_anchor")) {
    reviewReasons.push("No quoted North Carolina reference was grounded in the selected obituary candidate.");
  }
  const personSpecific = validCorroborators.filter((evidence) => isPersonSpecificCorroboratorField(evidence.field));
  const personSpecificFields = [...new Set(personSpecific.map((evidence) => evidence.field))] as PersonSpecificCorroboratorField[];
  if (!personSpecificFields.length) {
    reviewReasons.push(OBITUARY_NAME_PLUS_STATE_INSUFFICIENT_REASON);
  }
  const rejectedCorroborators = resolution.corroborators.flatMap((evidence) => {
    if (validCorroborators.includes(evidence) && isPersonSpecificCorroboratorField(evidence.field)) return [];
    if (validCorroborators.includes(evidence) && !isPersonSpecificCorroboratorField(evidence.field)) {
      return [{ field: evidence.field, candidateId: evidence.candidateId, reason: rejectedCorroboratorReason(evidence, packet, selected, candidateTextById) }];
    }
    return [{ field: evidence.field, candidateId: evidence.candidateId, reason: rejectedCorroboratorReason(evidence, packet, selected, candidateTextById) }];
  });
  // Competing still-plausible pages, and material conflicts on the selected
  // person, are real identity ambiguity. Notes on already-rejected pages are not.
  const competingPlausibleIds = new Set(
    resolution.candidateAssessments
      .filter((assessment) => assessment.disposition === "plausible_same_person" && !selected.has(assessment.candidateId))
      .map((assessment) => assessment.candidateId)
  );
  const materialConflictsOnLiveCandidates = resolution.conflicts.filter((conflict) =>
    conflict.material
    && !isMiddleNameConflictField(conflict.field)
    && (selected.has(conflict.candidateId) || competingPlausibleIds.has(conflict.candidateId))
  );
  const selectedMiddleConflicts = resolution.conflicts.filter((conflict) =>
    conflict.material && isMiddleNameConflictField(conflict.field) && selected.has(conflict.candidateId)
  );
  for (const conflict of selectedMiddleConflicts) {
    const reason = `Candidate ${conflict.candidateId} has a material middle-name conflict with the submitted owner.`;
    if (!reviewReasons.includes(reason)) reviewReasons.push(reason);
  }
  if (materialConflictsOnLiveCandidates.length || competingPlausibleIds.size > 0) {
    reviewReasons.push("The candidate evidence contains a material identity conflict.");
  }
  // “Ambiguous” is reserved for an actual, recorded identity conflict among
  // still-live candidates. A missing name/NC anchor, incomplete AI grouping,
  // or other unsupported evidence is simply insufficient to admit a match; it
  // is not evidence of two plausible decedents. Explicit middle-name mismatch
  // is a hard fail so the search ladder can continue.
  const hasMaterialConflict = materialConflictsOnLiveCandidates.length > 0
    || competingPlausibleIds.size > 0
    || hasDeterministicCandidateConflict;
  // Competing live pages stay ambiguous even if the model said no_match, so
  // automatic hop can still seek another provider instead of stamping a miss.
  const decision = hasMaterialConflict
    ? "ambiguous_review"
    : resolution.decision === "no_match"
      ? "no_match"
      : reviewReasons.length === 0 ? "confirmed_match"
        : "no_match";
  const acceptanceExplanation = explainObituaryIdentityAcceptance({
    decision,
    personSpecific,
    valid: validCorroborators,
    rejected: rejectedCorroborators,
    assessments: resolution.candidateAssessments,
    reviewReasons
  });
  return {
    resolution: { ...resolution, corroborators: validCorroborators, explanation: acceptanceExplanation.slice(0, 2_000) },
    decision,
    reviewReasons,
    corroboratingFields,
    personSpecificFields,
    rejectedCorroborators,
    acceptanceExplanation: acceptanceExplanation.slice(0, 2_000)
  };
}

export function renderObituarySourceEnvelope(envelope: ObituarySourceEnvelope): string {
  const parsed = ObituarySourceEnvelopeSchema.parse(envelope);
  const ordered = [...parsed.sources].sort((left, right) => left.sourceNumber - right.sourceNumber);
  if (new Set(ordered.map((source) => source.sourceNumber)).size !== ordered.length) throw new Error("Each obituary source must have a unique source number.");
  const text = ordered.map((source) => [
    `=== OBITUARY SOURCE ${source.sourceNumber} ===`,
    `Publisher: ${source.publisher}`,
    `URL: ${source.canonicalUrl}`,
    `Published: ${source.publicationDate ?? "Unknown"}`,
    "Core text:",
    source.coreText,
    `=== END OBITUARY SOURCE ${source.sourceNumber} ===`
  ].join("\n")).join("\n\n");
  if ([...text].length > OBITUARY_SOURCE_TEXT_LIMIT) throw new Error("obituary_text_too_large");
  return text;
}

/** Parses only the exact envelope format emitted by this package. */
export function parseObituarySourceEnvelope(value: unknown): Array<Pick<ObituarySource, "sourceNumber" | "publisher" | "canonicalUrl" | "publicationDate" | "coreText">> | null {
  const text = canonicalObituaryText(value);
  if (!text) return null;
  const expression = /^=== OBITUARY SOURCE (\d+) ===\nPublisher: ([^\n]+)\nURL: (https:\/\/[^\n]+)\nPublished: ([^\n]+)\nCore text:\n([\s\S]*?)\n=== END OBITUARY SOURCE \1 ===$/gm;
  const matches = [...text.matchAll(expression)];
  if (!matches.length || matches.map((match) => match[0]).join("\n\n") !== text) return null;
  const sources = matches.map((match) => ({
    sourceNumber: Number(match[1]), publisher: match[2]!.trim(), canonicalUrl: match[3]!.trim(),
    publicationDate: match[4] === "Unknown" ? null : match[4]!.trim(), coreText: match[5]!.trim()
  }));
  if (sources.some((source) => !source.coreText) || new Set(sources.map((source) => source.sourceNumber)).size !== sources.length) return null;
  return sources;
}

function survivors(graph: FamilyGraph, relationship: FamilyRelationship): FamilyPerson[] {
  return graph.persons.filter((person) => person.relationship === relationship && person.survival === "apparently_survived");
}

function isMateriallyAmbiguous(person: FamilyPerson): boolean {
  return person.ambiguityFlags.some((flag) => /step|foster|in-law|partner|fianc|former|considered|unclear/i.test(flag));
}

export function resolveApparentSpouse(graphInput: FamilyGraph): { status: SpouseStatus; people: FamilyPerson[]; evidence: string[] } {
  const graph = FamilyGraphSchema.parse(graphInput);
  // Extraction occasionally labels “a child and her spouse” as though the
  // spouse were related directly to the decedent. Those people cannot be
  // admitted into a count-only intestacy result without an explicit direct
  // marital statement about the decedent.
  const spouses = graph.persons.filter((person) =>
    person.relationship === "spouse"
    // A source-linked branch is stronger than wording heuristics: this person
    // is the spouse/partner of that named relative, not of the decedent.
    && !person.parentBranchId
    && !isNestedRelativeSpouse(person.evidenceExcerpt)
  );
  const survived = spouses.filter((person) => person.survival === "apparently_survived");
  const predeceased = spouses.filter((person) => person.survival === "apparently_predeceased");
  const evidence = spouses.map((person) => person.evidenceExcerpt);
  if (survived.length && !predeceased.length && !graph.sourceConflicts.length) return { status: "true", people: survived, evidence };
  if (!survived.length && predeceased.length && graph.maritalStatementComplete && !graph.sourceConflicts.length) return { status: "false", people: predeceased, evidence };
  return { status: "unknown", people: spouses, evidence };
}

function isNestedRelativeSpouse(evidence: string): boolean {
  // A spouse extracted from a relative list needs an explicit decedent-level
  // marital cue.  This catches child/sibling clauses (including “husband”
  // and parenthetical spouses) without rejecting ordinary direct statements
  // such as “survived by his wife, Jane”.
  if (/\b(?:survived|surviving|survives)\b[^.;]{0,80}\b(?:his|her|their)\s+(?:wife|husband|spouse)\b/i.test(evidence)) return false;
  return /\b(?:children?|daughters?|sons?|grandchildren?|brothers?|sisters?|siblings?)\b[^.;]{0,240}(?:\band\s+(?:his|her|their)\s+spouse|\band\s+(?:wife|husband)\b|\([^)]{1,80}\))/i.test(evidence);
}

function addHeir(person: FamilyPerson, reason: string, branch: string | null): ApparentHeir {
  return { id: person.id, name: person.name, relationship: person.relationship, survival: person.survival, branch, included: true, reason, evidenceExcerpt: person.evidenceExcerpt };
}

function exclude(person: FamilyPerson, reason: string): ApparentHeir {
  return { id: person.id, name: person.name, relationship: person.relationship, survival: person.survival, branch: person.parentBranchId ?? null, included: false, reason, evidenceExcerpt: person.evidenceExcerpt };
}

/**
 * Count-only North Carolina triage rule. It deliberately does not calculate
 * ownership shares, prove eligibility, or turn obituary language into law.
 */
export function calculateNcIntestacyTriage(graphInput: FamilyGraph, obituaryTextSha256: string, now = new Date()): NcIntestacyAnalysis {
  const graph = FamilyGraphSchema.parse(graphInput);
  if (!/^[a-f0-9]{64}$/.test(obituaryTextSha256)) throw new Error("obituary text hash must be SHA-256 hex");
  const spouse = resolveApparentSpouse(graph);
  const spouseHeirs = spouse.status === "true" ? spouse.people : [];
  const included: ApparentHeir[] = spouseHeirs.map((person) => addHeir(person, "apparently surviving spouse", null));
  const exclusions: ApparentHeir[] = [];
  const limitations = [...graph.extractionLimitations];
  let controllingHeirClass = spouseHeirs.length ? "surviving_spouse_plus_next_class" : "unresolved";
  let branchCount = 0;
  let nonSpouse: FamilyPerson[] = [];
  let complete = true;

  const livingChildren = survivors(graph, "child").filter((person) => !isMateriallyAmbiguous(person));
  const deceasedChildren = graph.persons.filter((person) => person.relationship === "child" && person.survival === "apparently_predeceased" && !isMateriallyAmbiguous(person));
  const childBranches = deceasedChildren.flatMap((child) => survivors(graph, "grandchild").filter((person) => person.parentBranchId === child.id && !isMateriallyAmbiguous(person)));
  const hasDescendantEvidence = livingChildren.length > 0 || deceasedChildren.length > 0 || survivors(graph, "grandchild").length > 0;

  if (hasDescendantEvidence) {
    controllingHeirClass = "descendants";
    nonSpouse = [...livingChildren, ...childBranches];
    branchCount = new Set([...livingChildren.map((person) => person.id), ...childBranches.map((person) => person.parentBranchId ?? person.id)]).size;
    for (const child of deceasedChildren) {
      if (!childBranches.some((person) => person.parentBranchId === child.id)) { complete = false; limitations.push(`No explicit eligible descendant branch was established for predeceased child ${child.name}.`); }
    }
    for (const person of graph.persons.filter((person) => ["parent", "sibling", "niece_nephew", "grandparent", "aunt_uncle", "cousin"].includes(person.relationship))) exclusions.push(exclude(person, "excluded because descendants are the controlling apparent class"));
  } else {
    const parents = survivors(graph, "parent").filter((person) => !isMateriallyAmbiguous(person));
    if (parents.length) {
      controllingHeirClass = "parents";
      nonSpouse = parents;
      branchCount = parents.length;
      for (const person of graph.persons.filter((person) => ["sibling", "niece_nephew", "grandparent", "aunt_uncle", "cousin"].includes(person.relationship))) exclusions.push(exclude(person, "excluded because parents are the controlling apparent class"));
    } else {
      const siblings = survivors(graph, "sibling").filter((person) => !isMateriallyAmbiguous(person));
      const deceasedSiblings = graph.persons.filter((person) => person.relationship === "sibling" && person.survival === "apparently_predeceased" && !isMateriallyAmbiguous(person));
      const siblingBranches = deceasedSiblings.flatMap((sibling) => survivors(graph, "niece_nephew").filter((person) => person.parentBranchId === sibling.id && !isMateriallyAmbiguous(person)));
      if (siblings.length || deceasedSiblings.length || siblingBranches.length) {
        controllingHeirClass = "siblings_and_representation";
        nonSpouse = [...siblings, ...siblingBranches];
        branchCount = new Set([...siblings.map((person) => person.id), ...siblingBranches.map((person) => person.parentBranchId ?? person.id)]).size;
        for (const sibling of deceasedSiblings) if (!siblingBranches.some((person) => person.parentBranchId === sibling.id)) { complete = false; limitations.push(`No explicit eligible descendant branch was established for predeceased sibling ${sibling.name}.`); }
      } else {
        const remote = graph.persons.filter((person) => ["grandparent", "aunt_uncle", "cousin"].includes(person.relationship) && person.survival === "apparently_survived" && !isMateriallyAmbiguous(person));
        if (remote.length) {
          controllingHeirClass = "remote_collateral_or_grandparent";
          nonSpouse = remote;
          branchCount = remote.length;
          complete = false;
          limitations.push("Remote collateral class is only a source-supported lower bound; paternal and maternal distribution cannot be established from obituary text alone.");
        }
      }
    }
  }

  for (const person of nonSpouse) included.push(addHeir(person, `included in apparent ${controllingHeirClass} class`, person.parentBranchId ?? null));
  for (const person of graph.persons) {
    if (included.some((entry) => entry.id === person.id) || exclusions.some((entry) => entry.id === person.id)) continue;
    if (person.survival !== "apparently_survived") exclusions.push(exclude(person, "not included because the obituary does not establish apparent survival"));
    else if (isMateriallyAmbiguous(person)) exclusions.push(exclude(person, "not included because obituary relationship is legally ambiguous"));
    else exclusions.push(exclude(person, "not included by the apparent controlling class"));
  }

  const supported = included.length;
  let heirCountStatus: HeirCountStatus = supported === 0 ? "insufficient_source_evidence" : complete && !graph.sourceConflicts.length ? "family_structure_complete" : "source_supported_lower_bound";
  const deathDate = graph.deathDate;
  const snapshot = ncIntestacyCountSnapshotForDeathDate(deathDate);
  const rulesetCovered = Boolean(snapshot);
  if (!rulesetCovered) {
    heirCountStatus = "historical_rules_unverified_current_snapshot_proxy";
    limitations.push(deathDate ? `Death date ${deathDate} is outside verified count-priority ruleset coverage.` : "Obituary did not supply a usable death date.");
  }

  const heirScore = scoreHeirEvidence(graph, included, controllingHeirClass, branchCount, spouse.status, heirCountStatus, rulesetCovered);
  const spouseScore = scoreSpouseEvidence(graph, spouse);
  return NcIntestacyAnalysisSchema.parse({
    result_type: NC_INTESTACY_ANALYSIS_RESULT_TYPE,
    version: "v1",
    obituaryTextSha256,
    familyGraph: graph,
    spouseStatus: spouse.status,
    apparentSpouseNames: spouse.people.map((person) => person.name),
    spouseEvidence: spouse.evidence,
    apparentHeirEstimate: supported,
    heirCountStatus,
    controllingHeirClass,
    branchCount,
    apparentHeirs: [...included, ...exclusions],
    rulesetId: snapshot?.id ?? "nc-intestacy-count-current-snapshot-proxy-v1",
    rulesetEffectiveFrom: snapshot?.from ?? null,
    rulesetEffectiveTo: snapshot?.to ?? null,
    heirConfidence: heirScore.score,
    spouseConfidence: spouseScore.score,
    heirConfidenceFactors: heirScore.factors,
    spouseConfidenceFactors: spouseScore.factors,
    limitations: [...new Set(limitations)]
  });
}

export function scoreHeirEvidence(graph: FamilyGraph, included: ApparentHeir[], controllingClass: string, branchCount: number, spouseStatus: SpouseStatus, status: HeirCountStatus, rulesetCovered: boolean): { score: number; factors: Record<string, number> } {
  const classEvidence = controllingClass === "unresolved" ? 0 : status === "family_structure_complete" ? 25 : 15;
  const survivalEvidence = included.length > 0 && included.every((person) => person.survival === "apparently_survived") ? 20 : 0;
  const needsBranch = ["descendants", "siblings_and_representation"].includes(controllingClass);
  const branchEvidence = !needsBranch || branchCount > 0 ? (status === "family_structure_complete" ? 20 : 10) : 0;
  const spouseEvidence = spouseStatus === "true" || spouseStatus === "false" ? 15 : 5;
  const enumerationEvidence = status === "family_structure_complete" ? 10 : included.length ? 5 : 0;
  const historicalCoverage = rulesetCovered ? 10 : 0;
  const factors = { controlling_class: classEvidence, survival: survivalEvidence, branch_assignment: branchEvidence, spouse_resolution: spouseEvidence, enumeration: enumerationEvidence, historical_rules: historicalCoverage };
  let score = Object.values(factors).reduce((sum, value) => sum + value, 0);
  if (graph.sourceConflicts.length || graph.persons.some(isMateriallyAmbiguous) || status === "source_supported_lower_bound") score = Math.min(score, 49);
  if (status === "insufficient_source_evidence") score = Math.min(score, 20);
  if (status === "historical_rules_unverified_current_snapshot_proxy") score = Math.min(score, 25);
  return { score, factors };
}

export function scoreSpouseEvidence(graph: FamilyGraph, spouse: ReturnType<typeof resolveApparentSpouse>): { score: number; factors: Record<string, number> } {
  const explicit = spouse.status === "true" || spouse.status === "false" ? 60 : spouse.people.length ? 30 : 0;
  const noConflict = graph.sourceConflicts.length ? 0 : 20;
  const sourceContext = graph.decedentName ? 10 : 5;
  const crossSource = graph.sourceConflicts.length ? 0 : 5;
  const factors = { marital_disposition: explicit, no_material_contradiction: noConflict, identity_matched_source_context: sourceContext, cross_source_support: crossSource };
  let score = Object.values(factors).reduce((sum, value) => sum + value, 0);
  if (spouse.status === "unknown" && spouse.people.length === 0) score = Math.min(score, 20);
  if (graph.sourceConflicts.length) score = Math.min(score, 49);
  return { score, factors };
}
